import React from 'react';
import get from 'lodash/get';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import AddIcon from '@atlaskit/icon/glyph/add';
import { usePartnerCatalogueContext } from '@css-xp/ppc-checkout/src/context/partner-catalogue-context';
import { PartnerQuoteLineItems } from '@css-xp/ppc-checkout/src/types';
import { FormDataForCatalogue, formatLineItems } from '@css-xp/ppc-checkout/src/utils';
import { isProductBelongsToJiraFamily } from '@css-xp/ppc-utils';
import { CartCatalogueLineItems, CatalogueContextItems } from '../types';
import './floating-order-summary.scss';

export type FloatingOrderSummary = {
  transactionAccountId: string;
  applicablePromotionId: string;
  isAddToOrderDisabled?: boolean;
  onCheckout: (OrderItems: CatalogueContextItems) => void;
  onProductSelect?: (lineItems: PartnerQuoteLineItems) => void;
  isJPDAsJiraFamilyEnabled?: boolean;
};

const hasNullValues = (itemAttribute: Record<string, any>) => {
  if (itemAttribute) {
    const keys = Object.keys(itemAttribute);
    return keys.filter((key) => itemAttribute[key]).length === keys.length;
  }
  return false;
};

const validateFormData = (formData: any): boolean => {
  const typedData = (formData as unknown) as FormDataForCatalogue;

  const { plan } = typedData;
  return hasNullValues(plan);
};

export const FloatingOrderSummary: React.FC<FloatingOrderSummary> = ({ onCheckout, isJPDAsJiraFamilyEnabled }) => {
  const {
    quoteItems: itemsInCart,
    clearCart,
    isCreateQuoteEnabled,
    quoteFormData,
    isPartnerCatalogueInformationLoading,
    getSelectedPricingPlanForAProduct,
    isCheckoutDisabled,
    productMaxUsersMap,
    isAnyCartItemInvalid,
    isInvalidIGForAllExistingEntitlements,
    getAppsParentEntitlement,
    productTrialMap,
    appsPromotionsIdsMap,
    appsPromotionsIdsDetailsMap,
  } = usePartnerCatalogueContext();

  const [areLineItemsHaveInvalidUsers, setLineItemsHaveInvalidUsers] = React.useState(false);
  const [isInvalidParentEntitlementLinkForApp, setIsInvalidParentEntitlementLinkForApp] = React.useState(false);

  const lineItems = formatLineItems(quoteFormData, getSelectedPricingPlanForAProduct, isJPDAsJiraFamilyEnabled);
  const cartCatalogueLineItems = [...lineItems] as CartCatalogueLineItems;
  const hasAllTheFieldsBeenSelected = validateFormData(quoteFormData);

  React.useEffect(() => {
    setLineItemsHaveInvalidUsers(false);
    setIsInvalidParentEntitlementLinkForApp(false);
    cartCatalogueLineItems.forEach((quoteItem) => {
      if (!quoteItem.estimatedNumberOfUsers || Number(quoteItem.estimatedNumberOfUsers) <= 0) {
        setLineItemsHaveInvalidUsers(true);
      }
      const maxUsers = productMaxUsersMap.get(quoteItem.id);
      if (maxUsers && quoteItem.estimatedNumberOfUsers > maxUsers) {
        setLineItemsHaveInvalidUsers(true);
      }

      if (quoteItem?.isApp) {
        const parentEntitlement = getAppsParentEntitlement(quoteItem?.productKey);
        quoteItem.isItAJiraProduct = isProductBelongsToJiraFamily(parentEntitlement?.productId, {
          isJPDAsJiraFamilyEnabled,
        });
        quoteItem.parentEntitlementId = parentEntitlement?.id ?? '';
        quoteItem.nextBillingTimestamp = parentEntitlement?.nextBillingTimestamp ?? '';
        if (!parentEntitlement) {
          setIsInvalidParentEntitlementLinkForApp(true);
        }
      }
    });
  }, [cartCatalogueLineItems, productMaxUsersMap]);

  const isReviewOrderButtonDisabled = React.useMemo(() => {
    return (
      isCheckoutDisabled ||
      !hasAllTheFieldsBeenSelected ||
      isPartnerCatalogueInformationLoading ||
      areLineItemsHaveInvalidUsers ||
      isAnyCartItemInvalid ||
      isInvalidParentEntitlementLinkForApp ||
      isInvalidIGForAllExistingEntitlements
    );
  }, [
    isCheckoutDisabled,
    hasAllTheFieldsBeenSelected,
    isPartnerCatalogueInformationLoading,
    areLineItemsHaveInvalidUsers,
    isAnyCartItemInvalid,
    isInvalidParentEntitlementLinkForApp,
    isInvalidIGForAllExistingEntitlements,
  ]);

  if (!(itemsInCart?.length > 0)) {
    return null;
  }

  const getTotalSelectedLineItems = () => {
    const numberOfItemsInTheCart = get(itemsInCart, 'length');
    return numberOfItemsInTheCart + ' ' + (numberOfItemsInTheCart === 1 ? 'item' : 'items') + ' selected';
  };

  return (
    <div className="floating-order-summary" data-testid="floating-order-summary-checkout">
      <div className="floating-order-summary-row-container">
        <Button
          testId={'floating-ordersummary-clear-btn'}
          appearance="subtle"
          className="floating-order-summary-row-container-clear-btn"
          onClick={clearCart}
          iconBefore={<CrossIcon label="Clear order" size="small" />}
        />
        <p data-testid="selected-item-label" className={'floating-order-summary-row-container-number-of-products'}>
          {getTotalSelectedLineItems()}
        </p>
        {isCreateQuoteEnabled && (
          <Button
            className={'floating-order-summary-row-container-add-btn'}
            appearance="primary"
            testId={'floating-ordersummary-add-btn'}
            isDisabled={isReviewOrderButtonDisabled}
            iconBefore={<AddIcon label="Add to order" size="small" />}
            onClick={() => {
              onCheckout({
                lineItems: cartCatalogueLineItems,
                productTrialMap,
                appsPromotionsIdsMap,
                appsPromotionsIdsDetailsMap,
              });
            }}
          >
            Add to order
          </Button>
        )}
      </div>
    </div>
  );
};
